/* eslint no-restricted-syntax: 0 */
/* global fetch, FormData, File */
import Immutable from 'immutable';
import {gql, ApolloClient, InMemoryCache, ApolloLink, HttpLink} from 'apollo-boost';

let jwt = '';

const setToken = ({token}) => {
  jwt = token;
};

const httpLink = new HttpLink({
  uri: process.env.GRAPHQL_API_URL,
});

const authLink = new ApolloLink((operation, forward) => {
  operation.setContext({
    headers: {
      Authorization: jwt,
    },
  });

  return forward(operation);
});

export const WSGQLCache = new InMemoryCache();
export const WSGQL = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: WSGQLCache,
  resolvers: {
    Mutation: {
      setSelectingFiles: (_root, {value}, {cache}) => {
        cache.writeData({data: {viewerState: {
          __typename: 'ViewerState',
          selectingFiles: value,
          selectedFiles: [],
        }}});
      },

      toggleFileSelection: (_root, {file}, {cache}) => {
        const query = gql`
          {
            viewerState @client {
              selectedFiles {
                id,
                name,
                thumbnailUrl,
                url,
              },
            },
          }
        `;
        const {viewerState} = cache.readQuery({query});

        let newSelectedFiles = [...viewerState.selectedFiles];
        if (viewerState.selectedFiles.find(f => f.id === file.id)) {
          newSelectedFiles = newSelectedFiles.filter(f => f.id !== file.id);
        } else {
          newSelectedFiles.push(file);
        }

        cache.writeData({data: {viewerState: {
          __typename: 'ViewerState',
          selectedFiles: newSelectedFiles,
        }}});
      },
    },
  },
  typeDefs: gql`
    extend type SelectedFile {
      id: ID!,
      name: String!,
      url: String!,
      thumbnailUrl: String!,
    }
  `,
});

// Initialize the local cache state
WSGQLCache.writeData({
  data: {
    viewerState: {
      __typename: 'ViewerState',
      selectingFiles: false,
      selectedFiles: [],
    },
  },
});

const toFormData = (obj, form, namespace) => {
  const fd = form || new FormData();
  let formKey;

  for (const property in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, property) && (obj[property] || obj[property] === '')) {
      if (namespace) {
        formKey = `${namespace}[${property}]`;
      } else {
        formKey = property;
      }

      // if the property is an object, but not a File, use recursivity.
      if (obj[property] instanceof Date) {
        fd.append(formKey, obj[property].toISOString());
      } else if (typeof obj[property] === 'object' && !(obj[property] instanceof File)) {
        toFormData(obj[property], fd, formKey);
      } else { // if it's a string or a File object
        fd.append(formKey, obj[property]);
      }
    }
  }

  return fd;
};

const formatError = (error) => {
  const t = error.get('title');
  const title = t ? `${t.charAt(0).toUpperCase()}${t.slice(1)}` : 'Error';

  return error.merge({
    title: title.replace('_', ' '),
    full_message: `${title} ${error.get('detail')}`,
  });
};

const call = ({method, route, params = {}, multipart = false}) => {
  if (['POST', 'PATCH'].includes(method) && !multipart) {
    // Allows for js object and immutable parameters
    params = params.toJSON ? JSON.stringify(params.toJSON()) : JSON.stringify(params);
  }

  let query = '';
  if (method === 'GET') {
    const queryItems = Object.keys(params).map((key) => {
      return `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`;
    });

    query = `?${queryItems.join('&')}`;
  }

  let formData;
  const headers = {Authorization: jwt};
  if (multipart) {
    formData = toFormData(params.toJS());
  } else {
    headers['Content-Type'] = 'application/json';
  }

  const operation = fetch(
    `${process.env.API_BASE}${route}.json${query.length > 1 ? query : ''}`,
    {
      method,
      headers,
      body: ['POST', 'PATCH'].includes(method) ? (formData || params) : undefined,
    },
  );

  return new Promise((resolve, reject) => {
    operation.then((resp) => {
      if (resp.status >= 200 && resp.status < 300) {
        resp.json().then((json) => {
          const immutableJson = Immutable.fromJS(json);

          return resolve({
            data: immutableJson.get('data'),
            included: immutableJson.get('included'),
          });
        });
      } else {
        resp.json().then((json) => {
          const errors = json.errors ? json.errors.map(e => e.title) : json.error;
          const error = new Error(`API reported error ${resp.status}: ${errors}`);

          if (json.errors) {
            error.errors = Immutable.fromJS(json.errors).map(formatError);
          } else {
            error.errors = Immutable.fromJS([{
              title: 'API Error',
              detail: `Reported code ${json.status}`,
            }]);
          }

          error.apiError = true;
          reject(error);
          throw error;
        });
      }
    });

    operation.catch(() => {
      const error = new Error('Could not fetch');

      error.errors = Immutable.fromJS([{
        title: 'API Error',
        detail: 'Could not connect',
      }]);

      reject(error);
    });
  });
};

const post = ({route, params, multipart = false}) => {
  return call({method: 'POST', route, params, multipart});
};

const get = ({route, params}) => {
  return call({method: 'GET', route, params});
};

const patch = ({route, params, multipart = false}) => {
  return call({method: 'PATCH', route, params, multipart});
};

const destroy = ({route, params}) => {
  return call({method: 'DELETE', route, params});
};

const parseApiItem = ({item, included}) => {
  const parseRelationshipItem = ({dataItem}) => {
    const includedItem = (included || Immutable.fromJS([])).find((i) => {
      return i.get('id') === dataItem.get('id');
    });

    if (includedItem) {
      return parseApiItem({item: includedItem, included});
    }

    // For some reason the item wasn't included
    return false;
  };

  // Converts the relationships map into a Map of "relationship name" -> [item]
  const relationships = item.get('relationships', new Immutable.Map()).map((relationship) => {
    // Handle both single and multi-item relationships
    const isSingleRelationship = Immutable.Iterable.isKeyed(relationship.get('data'));
    if (isSingleRelationship) {
      return parseRelationshipItem({dataItem: relationship.get('data')});
    }

    const data = relationship.get('data');
    if (!data) {
      return false;
    }

    return data.map((dataItem) => {
      return parseRelationshipItem({dataItem});
    }).filter(i => i !== false);
  }).filter(i => i !== false);

  return Immutable.fromJS({
    id: item.get('id'),
  }).merge(item.get('attributes'), relationships);
};


export default {
  call,
  post,
  get,
  patch,
  destroy,
  setToken,
  parseApiItem,
};
